/**
 * Sprint 12C — Waste Disposal Page
 *
 * Manages the lifecycle of collected waste (HW/SW/PF/Noil):
 *   PENDING → APPROVED → DISPATCHED
 *
 * Features:
 *  - KPI cards: Unreconciled waste by type
 *  - Table of disposal records with status badge & actions
 *  - Create Disposal Drawer (SOLD/REUSED_INTERNAL/SCRAPPED)
 *  - Approve & Dispatch inline buttons
 */
import React, { useState } from 'react'
import {
  Alert, Button, Card, Col, DatePicker, Drawer,
  Form, Input, InputNumber, Row, Select, Space, Statistic,
  Table, Tag, Tooltip, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReconciliationOutlined,
  RocketOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { usePlantId } from '@/shared/hooks/usePlantId'
import apiClient from '@/shared/api/client'

const { Title, Text } = Typography
const { Option } = Select

// ── Types ─────────────────────────────────────────────────────────────────────
interface WasteDisposal {
  id: string
  disposal_no: string
  plant_id: string
  disposal_date: string
  waste_type: string
  disposal_method: string
  weight_kg: number
  buyer_name: string | null
  rate_per_kg: number | null
  amount: number | null
  gate_pass_no: string | null
  vehicle_no: string | null
  period_from: string | null
  period_to: string | null
  remarks: string | null
  status: string
  approved_by: string | null
  approved_at: string | null
  created_at: string
}

interface UnreconciledSummary {
  waste_type: string
  total_kg: number
  record_count: number
}

// ── Labels ─────────────────────────────────────────────────────────────────────
const WASTE_TYPE_LABELS: Record<string, string> = {
  HW: 'Hard Waste',
  SW: 'Soft Waste',
  PF: 'Pneumafil',
  NO: 'Noil',
  RW: 'Rework Waste',
  FL: 'Fly/Cleaning',
  BR: 'Blowroom Droppings',
}

const METHOD_LABELS: Record<string, string> = {
  SOLD: 'Sold',
  REUSED_INTERNAL: 'Reused Internally',
  SCRAPPED: 'Scrapped',
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'orange',
  APPROVED: 'blue',
  DISPATCHED: 'green',
  CANCELLED: 'red',
}

// ── API calls ─────────────────────────────────────────────────────────────────
const api = {
  list: (plant_id: string) =>
    apiClient.get('/waste-disposal', { params: { plant_id } }).then(r => r.data as WasteDisposal[]),
  summary: (plant_id: string) =>
    apiClient.get('/waste-disposal/summary/unreconciled', { params: { plant_id } }).then(r => r.data as UnreconciledSummary[]),
  create: (body: object) =>
    apiClient.post('/waste-disposal', body).then(r => r.data),
  approve: (id: string) =>
    apiClient.post(`/waste-disposal/${id}/approve`).then(r => r.data),
  dispatch: (id: string, gate_pass_no?: string, vehicle_no?: string) =>
    apiClient.post(`/waste-disposal/${id}/dispatch`, { gate_pass_no, vehicle_no }).then(r => r.data),
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function WasteDisposalPage() {
  const { plantId } = usePlantId()
  const queryClient = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [dispatchModal, setDispatchModal] = useState<WasteDisposal | null>(null)
  const [form] = Form.useForm()
  const [dispatchForm] = Form.useForm()

  const disposalMethod = Form.useWatch('disposal_method', form)

  // ── Queries ──────────────────────────────────────────────────────────────────
  const { data: disposals = [], isLoading } = useQuery({
    queryKey: ['waste-disposals', plantId],
    queryFn: () => api.list(plantId!),
    enabled: !!plantId,
  })

  const { data: summary = [] } = useQuery({
    queryKey: ['waste-unreconciled', plantId],
    queryFn: () => api.summary(plantId!),
    enabled: !!plantId,
  })

  // ── Mutations ─────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: api.create,
    onSuccess: () => {
      message.success('Waste disposal record created')
      queryClient.invalidateQueries({ queryKey: ['waste-disposals', plantId] })
      queryClient.invalidateQueries({ queryKey: ['waste-unreconciled', plantId] })
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Create failed'),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approve(id),
    onSuccess: () => {
      message.success('Disposal approved — linked waste records reconciled')
      queryClient.invalidateQueries({ queryKey: ['waste-disposals', plantId] })
      queryClient.invalidateQueries({ queryKey: ['waste-unreconciled', plantId] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Approve failed'),
  })

  const dispatchMutation = useMutation({
    mutationFn: ({ id, gate_pass_no, vehicle_no }: { id: string; gate_pass_no?: string; vehicle_no?: string }) =>
      api.dispatch(id, gate_pass_no, vehicle_no),
    onSuccess: () => {
      message.success('Disposal dispatched — gate pass issued')
      queryClient.invalidateQueries({ queryKey: ['waste-disposals', plantId] })
      setDispatchModal(null)
      dispatchForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Dispatch failed'),
  })

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      createMutation.mutate({
        ...values,
        plant_id: plantId,
        disposal_date: values.disposal_date?.format('YYYY-MM-DD'),
        period_from: values.period_from?.format('YYYY-MM-DD'),
        period_to: values.period_to?.format('YYYY-MM-DD'),
      })
    } catch (_) { /* validation error */ }
  }

  const handleDispatch = async () => {
    if (!dispatchModal) return
    const values = dispatchForm.getFieldsValue()
    dispatchMutation.mutate({
      id: dispatchModal.id,
      gate_pass_no: values.gate_pass_no,
      vehicle_no: values.vehicle_no,
    })
  }

  // ── KPI cards from summary ────────────────────────────────────────────────────
  const totalUnreconciled = summary.reduce((s, r) => s + r.total_kg, 0)

  // ── Table columns ─────────────────────────────────────────────────────────────
  const columns: ColumnsType<WasteDisposal> = [
    {
      title: 'Disposal No',
      dataIndex: 'disposal_no',
      key: 'disposal_no',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'disposal_date',
      key: 'disposal_date',
    },
    {
      title: 'Waste Type',
      dataIndex: 'waste_type',
      key: 'waste_type',
      render: (v: string) => <Tag color="purple">{WASTE_TYPE_LABELS[v] ?? v}</Tag>,
    },
    {
      title: 'Method',
      dataIndex: 'disposal_method',
      key: 'disposal_method',
      render: (v: string) => METHOD_LABELS[v] ?? v,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      align: 'right' as const,
      render: (v: number) => Number(v).toFixed(3),
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer_name',
      key: 'buyer_name',
      render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Amount (₹)',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: (v: number | null) =>
        v != null ? <Text type="success">₹{Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text> : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, row: WasteDisposal) => (
        <Space>
          {row.status === 'PENDING' && (
            <Tooltip title="Approve & reconcile waste records">
              <Button
                size="small" type="primary" ghost
                icon={<CheckCircleOutlined />}
                loading={approveMutation.isPending}
                onClick={() => approveMutation.mutate(row.id)}
              >
                Approve
              </Button>
            </Tooltip>
          )}
          {row.status === 'APPROVED' && (
            <Tooltip title="Issue gate pass & mark as dispatched">
              <Button
                size="small" type="primary"
                icon={<RocketOutlined />}
                onClick={() => { setDispatchModal(row); dispatchForm.resetFields() }}
              >
                Dispatch
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <ReconciliationOutlined style={{ marginRight: 8 }} />
            Waste Disposal
          </Title>
          <Text type="secondary">Manage waste sale, reuse, and scrapping</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)}>
            New Disposal
          </Button>
        </Col>
      </Row>

      {/* Unreconciled Waste Alert */}
      {totalUnreconciled > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message={`${totalUnreconciled.toFixed(0)} kg of waste across ${summary.length} type(s) is unreconciled — create a disposal record to close.`}
          description={
            <Space wrap>
              {summary.map(s => (
                <Tag key={s.waste_type} color="orange">
                  {WASTE_TYPE_LABELS[s.waste_type] ?? s.waste_type}: {s.total_kg.toFixed(1)} kg ({s.record_count} shifts)
                </Tag>
              ))}
            </Space>
          }
        />
      )}

      {/* KPI Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Disposals"
              value={disposals.length}
              prefix={<ReconciliationOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Pending Approval"
              value={disposals.filter(d => d.status === 'PENDING').length}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Unreconciled (kg)"
              value={totalUnreconciled.toFixed(0)}
              valueStyle={{ color: totalUnreconciled > 0 ? '#cf1322' : '#3f8600' }}
              suffix="kg"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Revenue (₹)"
              value={
                disposals
                  .filter(d => d.disposal_method === 'SOLD' && d.amount)
                  .reduce((s, d) => s + Number(d.amount ?? 0), 0)
                  .toLocaleString('en-IN', { maximumFractionDigits: 0 })
              }
              prefix="₹"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Table
        rowKey="id"
        dataSource={disposals}
        columns={columns}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        bordered
        size="small"
      />

      {/* Create Drawer */}
      <Drawer
        title="New Waste Disposal"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={480}
        extra={
          <Button type="primary" onClick={handleCreate} loading={createMutation.isPending}>
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical" initialValues={{ disposal_method: 'SOLD', waste_type: 'SW' }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="waste_type" label="Waste Type" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(WASTE_TYPE_LABELS).map(([v, l]) => (
                    <Option key={v} value={v}>{l}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="disposal_method" label="Disposal Method" rules={[{ required: true }]}>
                <Select>
                  <Option value="SOLD">Sold</Option>
                  <Option value="REUSED_INTERNAL">Reused Internally</Option>
                  <Option value="SCRAPPED">Scrapped</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="disposal_date" label="Disposal Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="weight_kg" label="Weight (kg)" rules={[{ required: true }]}>
                <InputNumber min={0.001} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="period_from" label="Period From">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="period_to" label="Period To">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {disposalMethod === 'SOLD' && (
            <>
              <Form.Item name="buyer_name" label="Buyer Name">
                <Input placeholder="Buyer / waste dealer name" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="rate_per_kg" label="Rate (₹/kg)">
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="₹" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="amount" label="Amount (₹)">
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="₹" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="gate_pass_no" label="Gate Pass No">
                    <Input placeholder="GP-2026-001" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="vehicle_no" label="Vehicle No">
                    <Input placeholder="MH-XX-0000" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Dispatch Modal */}
      <Drawer
        title={`Dispatch — ${dispatchModal?.disposal_no}`}
        open={!!dispatchModal}
        onClose={() => { setDispatchModal(null); dispatchForm.resetFields() }}
        width={360}
        extra={
          <Button type="primary" onClick={handleDispatch} loading={dispatchMutation.isPending}>
            Confirm Dispatch
          </Button>
        }
      >
        {dispatchModal && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Marking ${Number(dispatchModal.weight_kg).toFixed(3)} kg of ${WASTE_TYPE_LABELS[dispatchModal.waste_type] ?? dispatchModal.waste_type} as dispatched`}
            />
            <Form form={dispatchForm} layout="vertical">
              <Form.Item name="gate_pass_no" label="Gate Pass No">
                <Input placeholder="GP-2026-001" />
              </Form.Item>
              <Form.Item name="vehicle_no" label="Vehicle No">
                <Input placeholder="MH-XX-0000" />
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  )
}
